import type { ZohoContact } from '@deltasuite/zoho-client';
import { useEffect, useState } from 'react';
import { getScript } from '../../api/scripts';
import { DatabaseUpdatePanel } from './DatabaseUpdatePanel';
import { DISPOSITION_LABELS, type Disposition } from './types';
import { preferredNumber, telHref } from './preferredNumber';
import { useIdleNudge, type NudgeLevel } from './useIdleNudge';

const FALLBACK_SCRIPT =
  "Hi [contact name], it's [you] from Zanda. Reaching out about our latest commercial range — got two minutes?";

interface Props {
  index: number;
  total: number;
  contact: ZohoContact;
  callPhase: 'pre' | 'live';
  callStartedAt: number | null;
  pointsScored: number;
  pointTarget: number;
  vmeTemplateLoaded: boolean;
  onDial: () => void;
  onDispose: (input: DisposeInput) => void;
}

export type DisposeInput =
  | { kind: 'telesales'; subject: string; notes: string; durationSeconds: number }
  | { kind: 'courtesy_call'; subject: string; notes: string; durationSeconds: number }
  | { kind: 'vme'; durationSeconds: number }
  | { kind: 'no_answer'; durationSeconds: number }
  | {
      kind: 'database_update';
      notes?: string;
      /** Sibling contact ids to also flag Dropped=true. Worker enqueues a
       * contact_update write_queue row per id. */
      dropSiblings: readonly string[];
      durationSeconds: number;
    };

export function ContactCard(props: Props) {
  const { contact, callPhase } = props;
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [script, setScript] = useState<string>(FALLBACK_SCRIPT);
  const [showDbUpdate, setShowDbUpdate] = useState(false);
  const cardLoadedAt = props.contact.id; // re-mount per contact resets state
  const contactType = contact.Company_Type ?? '';

  useEffect(() => {
    setSubject('');
    setNotes('');
    setShowDbUpdate(false);
  }, [cardLoadedAt]);

  // Fetch the per-contact-type script when the card mounts or the contact's
  // Company_Type changes. R2's /v1/scripts endpoint owns the lookup; the
  // mock fallback in api/scripts.ts kicks in when no worker is configured.
  useEffect(() => {
    if (!contactType) {
      setScript(FALLBACK_SCRIPT);
      return;
    }
    let cancelled = false;
    void getScript(contactType)
      .then((res) => {
        if (cancelled) return;
        setScript(res.body ?? FALLBACK_SCRIPT);
      })
      .catch(() => {
        if (cancelled) return;
        setScript(FALLBACK_SCRIPT);
      });
    return () => {
      cancelled = true;
    };
  }, [contactType]);

  // Idle nudge timer is the time the user has been on the card. Resets each
  // time we get a new contact.
  const [activeSince] = useState(() => Date.now());
  const nudge = useIdleNudge(activeSince, callPhase === 'pre');

  const number = preferredNumber(contact);

  const onTelesales = () => {
    if (!subject.trim()) return;
    const durationSeconds = Math.floor((Date.now() - (props.callStartedAt ?? Date.now())) / 1000);
    props.onDispose({
      kind: 'telesales',
      subject: subject.trim(),
      notes: notes.trim(),
      durationSeconds,
    });
  };
  const onCourtesy = () => {
    if (!subject.trim()) return;
    const durationSeconds = Math.floor((Date.now() - (props.callStartedAt ?? Date.now())) / 1000);
    props.onDispose({
      kind: 'courtesy_call',
      subject: subject.trim(),
      notes: notes.trim(),
      durationSeconds,
    });
  };
  const onVme = () => {
    if (!props.vmeTemplateLoaded) return;
    const durationSeconds = Math.floor((Date.now() - (props.callStartedAt ?? Date.now())) / 1000);
    // Brief §8: open mailto + log + advance, all in one click.
    const to = contact.Email ?? '';
    const firstName = contact.First_Name ?? 'there';
    const subject = encodeURIComponent('Following up after voicemail');
    const body = encodeURIComponent(
      `Hi ${firstName},\n\nTried to call earlier and left a quick voicemail. Wanted to follow up on the Ember Bronze series — thought your team would want a look.\n\nBrochure attached.\n\nBest,\n— Brent`,
    );
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, '_blank');
    props.onDispose({ kind: 'vme', durationSeconds });
  };
  const onNoAnswer = () => {
    const durationSeconds = Math.floor((Date.now() - (props.callStartedAt ?? Date.now())) / 1000);
    props.onDispose({ kind: 'no_answer', durationSeconds });
  };
  const onDbUpdateConfirm = (input: { notes: string; dropSiblings: readonly string[] }) => {
    const durationSeconds = Math.floor((Date.now() - (props.callStartedAt ?? Date.now())) / 1000);
    setShowDbUpdate(false);
    props.onDispose({
      kind: 'database_update',
      notes: input.notes || undefined,
      dropSiblings: input.dropSiblings,
      durationSeconds,
    });
  };

  return (
    <main className="min-h-full px-8 py-10">
      <header className="flex items-baseline justify-between">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-graphite-500">
          Contact <span className="text-graphite-900">{props.index + 1}</span> of {props.total}
        </p>
        <p className="font-mono text-xs text-graphite-500">
          <span className="text-graphite-900">{props.pointsScored}</span> / {props.pointTarget} pts
        </p>
      </header>

      <NudgeBar level={nudge} />

      <section className="mt-8 grid gap-10 lg:grid-cols-[2fr_3fr]">
        <div>
          <h1 className="font-sans text-3xl font-semibold tracking-tight text-graphite-900">
            {[contact.First_Name, contact.Last_Name].filter(Boolean).join(' ')}
          </h1>
          <p className="mt-1 font-sans text-sm text-graphite-700">
            {contact.Title ? `${contact.Title} · ` : ''}
            {contact.Account_Name?.name ?? ''}
          </p>

          <div className="mt-8">
            {callPhase === 'pre' ? (
              <a
                href={number ? telHref(number.value) : undefined}
                onClick={(e) => {
                  if (!number) {
                    e.preventDefault();
                    return;
                  }
                  props.onDial();
                }}
                className={
                  'flex w-full items-baseline justify-between rounded-md px-5 py-4 font-sans text-lg font-semibold transition-colors ' +
                  (number
                    ? 'bg-signal-amber text-surface hover:bg-signal-amber-hover'
                    : 'bg-graphite-100 text-graphite-500')
                }
              >
                <span>{number ? `Dial ${number.value}` : 'No number on file'}</span>
                {number ? (
                  <span className="font-mono text-xs uppercase tracking-wider opacity-80">
                    {number.kind}
                  </span>
                ) : null}
              </a>
            ) : (
              <div className="rounded-md border border-graphite-100 bg-surface-sunk px-4 py-3 font-mono text-xs text-graphite-700">
                On call · started{' '}
                {props.callStartedAt
                  ? new Date(props.callStartedAt).toLocaleTimeString('en-AU', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })
                  : '—'}
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-6">
          <div>
            <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
              Script
            </h3>
            <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-graphite-100 bg-surface-sunk p-4 font-sans text-sm leading-relaxed text-graphite-700">
              {script}
            </pre>
          </div>

          <div>
            <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
              Notes
            </h3>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject (required for Telesales / Courtesy)"
              className="mt-2 w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
            />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional free-text notes…"
              rows={4}
              className="mt-2 w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
            />
          </div>
        </div>
      </section>

      <footer className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <DispButton
          kind="telesales"
          tone="primary"
          disabled={!subject.trim()}
          onClick={onTelesales}
        >
          {DISPOSITION_LABELS.telesales} <Pts n={3} />
        </DispButton>
        <DispButton
          kind="courtesy_call"
          tone="primary"
          disabled={!subject.trim()}
          onClick={onCourtesy}
        >
          {DISPOSITION_LABELS.courtesy_call} <Pts n={2} />
        </DispButton>
        <DispButton
          kind="vme"
          tone="amber"
          disabled={!props.vmeTemplateLoaded}
          onClick={onVme}
          title={props.vmeTemplateLoaded ? undefined : 'Pick a campaign template first'}
        >
          {DISPOSITION_LABELS.vme} <Pts n={1} />
        </DispButton>
        <DispButton kind="no_answer" tone="ghost" onClick={onNoAnswer}>
          {DISPOSITION_LABELS.no_answer}
        </DispButton>
        <DispButton kind="database_update" tone="primary" onClick={() => setShowDbUpdate(true)}>
          {DISPOSITION_LABELS.database_update} <Pts n={3} />
        </DispButton>
      </footer>

      {showDbUpdate ? (
        <DatabaseUpdatePanel
          primary={contact}
          onConfirm={onDbUpdateConfirm}
          onCancel={() => setShowDbUpdate(false)}
        />
      ) : null}
    </main>
  );
}

function Pts({ n }: { n: number }) {
  return <span className="ml-1 font-mono text-[0.65rem] opacity-70">+{n}</span>;
}

function DispButton({
  tone,
  disabled,
  onClick,
  title,
  children,
}: {
  kind: Disposition;
  tone: 'primary' | 'amber' | 'ghost';
  disabled?: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  const cls =
    tone === 'primary'
      ? 'bg-graphite-900 text-surface hover:bg-graphite-700'
      : tone === 'amber'
        ? 'bg-signal-amber text-surface hover:bg-signal-amber-hover'
        : 'bg-surface text-graphite-700 border border-graphite-300 hover:bg-surface-sunk';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`rounded-md px-3 py-3 font-sans text-sm font-medium transition-colors disabled:opacity-40 ${cls}`}
    >
      {children}
    </button>
  );
}

function NudgeBar({ level }: { level: NudgeLevel }) {
  if (level === 'none') return null;
  const base =
    'mt-4 rounded-md border px-4 py-2 font-mono text-xs ' +
    (level === 'soft'
      ? 'border-graphite-300 bg-surface-sunk text-graphite-700'
      : 'border-signal-amber/50 bg-signal-amber/10 text-signal-amber');
  const text =
    level === 'soft'
      ? "Pressure on — next number's waiting."
      : level === 'hard'
        ? "You've stopped. Tom is still going."
        : 'Sprint paused at 5 minutes idle.';
  return (
    <p role="status" className={base}>
      {text}
    </p>
  );
}
